/**
 * District facet page (`/districts/:slug/rent`, `/districts/:slug/buy`) —
 * the 「{district}租盤 / {district}樓盤」 pages (SEO F9).
 *
 * One component for both facets: they differ only in the transaction mode
 * their loader requested, and that is already reflected in the SEO the
 * backend returned. A second component would be two places to forget the
 * back-link to the hub.
 *
 * Two links, and they point in different directions on purpose. The hub
 * back-link is this page's OUTBOUND edge; it was once described here as what
 * "keeps the facet inside the crawl mesh", which was simply false — an
 * outbound link creates no inbound one, and for two releases every district
 * facet was a sitemap-only orphan with zero inbound links anywhere in
 * `web/apps/public` or `web/packages`. The INBOUND edges are the hub's own
 * links down (`districts/pages/detail.tsx`) and the sibling cross-link below:
 * `/districts/x/rent` and `/districts/x/buy` are different documents, so each
 * one linking to the other is a real inbound link for the other.
 *
 * `indexable_route_has_inbound_link` (lintbazelinvariants) now holds the
 * general rule, so the class cannot return silently.
 *
 * ★ THIS PAGE IS NOT A `canvas`, and it was declared one for two rounds
 * (#6915 / N1). It renders three things in flow — a heading column, the
 * map/list canvas, and `DistrictFacetAbout`, an entire SEO prose block — and a
 * `canvas` row bounds `main` to the viewport and clips it. `ListingsClient` is
 * `h-full` and a flex ITEM, so the default `flex-shrink: 1` handed it whatever
 * its siblings left: measured on the hermetic fleet at 393x852, the canvas box
 * was **y=300.5 height=9.25** in an 852-tall `main`, and 106px of a 656px main
 * at 1280x720. Nine pixels of results on an indexable page, with nothing red
 * anywhere.
 *
 * The row is `article` now — the same row `district-detail/index` beside it
 * takes — so the footer appears, the document scrolls, the shell draws the
 * mobile back band, and the canvas is a BLOCK with an explicit height
 * (`ListingsClient`'s `surface="inline"`). The page-local repair the old note
 * here reached for does not exist in either direction, which is why the remedy
 * was a chrome row.
 */
import { SeoHead } from '@/shared/components/SeoHead';
import { Link } from '@/shared/i18n/routing';
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import { routes } from '@letsgethome/routes';
import { plaintextString } from '@letsgethome/types/branded';
import { PageContainer } from '@letsgethome/ui/page-container';
import { useLoaderData } from 'react-router';

import { ListingsClient } from '../../listings/components/ListingsClient';
import { useListingsPageHref } from '../../listings/components/ListingsPager';
import { DistrictFacetAbout, DistrictFacetLede } from '../components/DistrictFacetProse';
import type { loader } from './rent.loader';

export function Component() {
  const {
    listings,
    totalCount,
    page,
    perPage,
    hasMore,
    standing,
    mapPosition,
    seo,
    district,
    mode,
    filterSet,
  } = useLoaderData<typeof loader>();
  // The district pages share the `estates` string namespace (see
  // `districts/pages/detail.tsx`) — one namespace for the browse surface.
  const t = useTranslations('estates');
  const locale = useLocale();
  // The facet's own base route, so `?page=2` stays this district's page.
  const hrefForPage = useListingsPageHref(
    mode === 'rent' ? routes.districts.rent(district.slug) : routes.districts.buy(district.slug)
  );

  return (
    <>
      <SeoHead {...seo} />
      <PageContainer size="7xl" className="pt-6 sm:px-6 lg:px-8">
        {/* The h1 mirrors the SEO title, as on the other index pages. */}
        <h1 className="text-3xl font-bold text-foreground sm:text-4xl">{seo.title}</h1>
        <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link
            href={routes.districts.detail(district.slug)}
            prefetch="intent"
            className="text-success-text underline-offset-2 hover:underline"
          >
            {t('districtFacet.backToHub', { district: plaintextString(district.name) })}
          </Link>
          {/* The sibling facet — the other transaction mode for the SAME
              district. Derived from `mode` rather than passed in, so the two
              loaders cannot disagree about which page is the sibling. */}
          {mode === 'rent' ? (
            <Link
              href={routes.districts.buy(district.slug)}
              prefetch="intent"
              className="text-info-text underline-offset-2 hover:underline"
            >
              {t('districtFacet.buyLink', { district: plaintextString(district.name) })}
            </Link>
          ) : (
            <Link
              href={routes.districts.rent(district.slug)}
              prefetch="intent"
              className="text-success-text underline-offset-2 hover:underline"
            >
              {t('districtFacet.rentLink', { district: plaintextString(district.name) })}
            </Link>
          )}
        </p>
        {/* Server-rendered prose, ABOVE the client-hydrated grid: this is the
            only text a crawler sees before the listings hydrate. */}
        <DistrictFacetLede
          slug={district.slug}
          districtName={district.name}
          locale={locale}
          mode={mode}
          totalCount={totalCount}
        />
      </PageContainer>
      {/* The canvas is a BLOCK in this document, inside the same 7xl reading
          column as the heading above it and the prose below — which is what
          `article`'s `width: 'contained'` means, and what a full-bleed map
          under a contained navbar was not. Its height is
          `ListingsClient`'s to spell from `surface`, once, rather than a class
          list restated at every call site. */}
      <PageContainer size="7xl" className="mt-6 sm:px-6 lg:px-8">
        <ListingsClient
          surface="inline"
          initialListings={listings}
          initialMeta={{ totalCount, page, perPage, hasMore, standing }}
          initialMapPosition={mapPosition}
          mode={mode}
          filters={filterSet}
          hrefForPage={hrefForPage}
        />
      </PageContainer>
      <DistrictFacetAbout
        slug={district.slug}
        districtName={district.name}
        locale={locale}
        mode={mode}
      />
    </>
  );
}
